(() => {
  if (window.__OWProductSizeFitModalBound) return;
  window.__OWProductSizeFitModalBound = true;

  const SIZE_CHART_UNIT_STORAGE_KEY = 'outfitworks:size-chart-unit';
  const SIZE_CHART_DEFAULT_UNIT = 'in';

  const findRoot = (element) => element?.closest?.('[data-product-size-fit-root]');
  const normalizeUnit = (unit) => (unit === 'cm' ? 'cm' : 'in');

  const getStoredUnit = () => {
    try {
      return normalizeUnit(localStorage.getItem(SIZE_CHART_UNIT_STORAGE_KEY));
    } catch (error) {
      return SIZE_CHART_DEFAULT_UNIT;
    }
  };

  const storeUnit = (unit) => {
    try {
      localStorage.setItem(SIZE_CHART_UNIT_STORAGE_KEY, normalizeUnit(unit));
    } catch (error) {}
  };

  const formatInches = (value) => (Math.round(value * 10) / 10).toFixed(1);
  const formatCentimeters = (value) => String(Number(value));

  const getMeasurementValues = (rawValue) => {
    const value = String(rawValue ?? '').trim();

    if (!value || /^(?:-|n\/a|na)$/i.test(value)) {
      return null;
    }

    const numericValue = value.match(/^(-?\d+(?:\.\d+)?)\s*(?:cm)?$/i);
    if (numericValue) {
      const centimeters = Number(numericValue[1]);

      return {
        cm: formatCentimeters(centimeters),
        in: formatInches(centimeters * 0.3937007874),
      };
    }

    const rangeValue = value.match(/^(-?\d+(?:\.\d+)?)\s*[-–]\s*(-?\d+(?:\.\d+)?)\s*(?:cm)?$/i);
    if (rangeValue) {
      const startCentimeters = Number(rangeValue[1]);
      const endCentimeters = Number(rangeValue[2]);

      return {
        cm: `${formatCentimeters(startCentimeters)}-${formatCentimeters(endCentimeters)}`,
        in: `${formatInches(startCentimeters * 0.3937007874)}-${formatInches(endCentimeters * 0.3937007874)}`,
      };
    }

    return null;
  };

  const prepareMeasurementCell = (cell) => {
    if (!(cell instanceof HTMLElement)) return;

    const originalValue = cell.dataset.valueCm || cell.textContent || '';
    const convertedValues = getMeasurementValues(originalValue);

    if (!convertedValues) {
      cell.dataset.valueCm = originalValue.trim();
      cell.dataset.valueIn = originalValue.trim();
      cell.dataset.sizeMeasurementConvertible = 'false';
      return;
    }

    cell.dataset.valueCm = convertedValues.cm;
    cell.dataset.valueIn = convertedValues.in;
    cell.dataset.sizeMeasurementConvertible = 'true';
  };

  const updateSizeChartUnit = (root, unit) => {
    if (!(root instanceof HTMLElement)) return;

    const activeUnit = normalizeUnit(unit);
    root.dataset.sizeChartUnit = activeUnit;

    root.querySelectorAll('[data-size-measurement-cell]').forEach((cell) => {
      if (!(cell instanceof HTMLElement)) return;
      prepareMeasurementCell(cell);

      const nextValue = activeUnit === 'cm' ? cell.dataset.valueCm : cell.dataset.valueIn;
      cell.textContent = nextValue || cell.dataset.valueCm || '';
    });

    root.querySelectorAll('[data-size-measurement-heading]').forEach((heading) => {
      if (!(heading instanceof HTMLElement)) return;

      const label = heading.dataset.label || heading.textContent.replace(/\s*\((?:in|cm)\)\s*$/i, '').trim();
      const unitLabel = heading.querySelector('[data-size-unit-label]');

      heading.dataset.label = label;

      if (unitLabel) {
        unitLabel.textContent = `(${activeUnit})`;
      } else {
        heading.textContent = `${label} (${activeUnit})`;
      }
    });

    root.querySelectorAll('[data-size-unit-button]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      const isActive = normalizeUnit(button.dataset.sizeUnitButton) === activeUnit;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
  };

  const initSizeChartUnits = (container = document) => {
    const roots = [];

    if (container instanceof HTMLElement && container.matches('[data-product-size-fit-root]')) {
      roots.push(container);
    }

    container.querySelectorAll?.('[data-product-size-fit-root]').forEach((root) => {
      roots.push(root);
    });

    const unit = getStoredUnit();
    roots.forEach((root) => updateSizeChartUnit(root, unit));
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const unitButton = target?.closest('[data-size-unit-button]');
    if (unitButton instanceof HTMLButtonElement) {
      const unit = normalizeUnit(unitButton.dataset.sizeUnitButton);
      storeUnit(unit);
      document.querySelectorAll('[data-product-size-fit-root]').forEach((root) => updateSizeChartUnit(root, unit));
      return;
    }

    const openButton = target?.closest('[data-size-help-open]');
    if (openButton instanceof HTMLButtonElement) {
      const modal = findRoot(openButton)?.querySelector('[data-size-help-modal]');
      if (modal instanceof HTMLDialogElement && typeof modal.showModal === 'function') {
        modal.showModal();
      }
      return;
    }

    const closeButton = target?.closest('[data-size-help-close]');
    if (closeButton instanceof HTMLButtonElement) {
      closeButton.closest('[data-size-help-modal]')?.close();
      return;
    }

    if (!(event.target instanceof HTMLDialogElement) || !event.target.matches('[data-size-help-modal]')) {
      return;
    }

    const modal = event.target;
    const rect = modal.getBoundingClientRect();
    const clickedInside =
      rect.top <= event.clientY &&
      event.clientY <= rect.top + rect.height &&
      rect.left <= event.clientX &&
      event.clientX <= rect.left + rect.width;

    if (!clickedInside) {
      modal.close();
    }
  });

  initSizeChartUnits();

  if ('MutationObserver' in window) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            initSizeChartUnits(node);
          }
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
